import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createSeededRandom } from '../../utils/random';
import { hullTextures } from './HullTextures';

// Procedural capital-ship generator. Frame: +Z = stern, −Z = bow, +Y = dorsal, +X = starboard. Metres.

export type FrigateParams = {
  seed: number;
  length: number;
  width: number; // × length
  height: number; // × length
  prow: number; // fraction of length taken by the tapering bow
  sponsons: boolean;
  wingSpan: number; // × length (0 = none)
  wingSweep: number; // metres of sweep at the tip
  dorsalFin: boolean;
  engines: { cols: number; rows: number };
  turrets: number;
  dish: boolean;
  registry: string;
};

export type FrigateModel = {
  root: THREE.Group;
  bridge: THREE.Group;
  /** engine nozzle exits in ship space (for plumes) */
  engines: { position: THREE.Vector3; radius: number }[];
  /** bridge window eye point for the first-person camera */
  cockpitEye: THREE.Vector3;
  size: THREE.Vector3;
  navLights: THREE.Sprite[];
  strobes: THREE.Sprite[];
  triangles: number;
};

type Section = { z: number; w: number; h: number; c: number; y: number };
type MatKey = 'hull' | 'dark' | 'accent' | 'engine' | 'glow';

// ---------- geometry helpers ----------

function ring(s: Section): THREE.Vector3[] {
  const { w, h, y, z } = s;
  const c = Math.min(w, h) * s.c;
  const hw = w / 2, hh = h / 2;
  return [
    [-hw + c, hh], [hw - c, hh], [hw, hh - c], [hw, -hh + c],
    [hw - c, -hh], [-hw + c, -hh], [-hw, -hh + c], [-hw, hh - c],
  ].map(([x, yy]) => new THREE.Vector3(x, yy + y, z));
}

/** Pushes a triangle, flipping it so its normal points away from `inside`. */
function tri(out: number[], a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, inside: THREE.Vector3): void {
  const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a));
  const centroid = new THREE.Vector3().add(a).add(b).add(c).divideScalar(3);
  if (n.dot(centroid.sub(inside)) < 0) out.push(a.x, a.y, a.z, c.x, c.y, c.z, b.x, b.y, b.z);
  else out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
}

/** Lofted hull through chamfered sections, capped at both ends. */
function loft(sections: Section[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const rings = sections.map(ring);
  for (let i = 0; i < rings.length - 1; i++) {
    const r0 = rings[i], r1 = rings[i + 1];
    const inside = new THREE.Vector3(0, (sections[i].y + sections[i + 1].y) / 2, (sections[i].z + sections[i + 1].z) / 2);
    for (let k = 0; k < 8; k++) {
      const k2 = (k + 1) % 8;
      tri(pos, r0[k], r0[k2], r1[k2], inside);
      tri(pos, r0[k], r1[k2], r1[k], inside);
    }
  }
  for (const [idx, dir] of [[0, 1], [rings.length - 1, -1]] as const) {
    const r = rings[idx];
    const c = new THREE.Vector3(0, sections[idx].y, sections[idx].z);
    const inside = c.clone().add(new THREE.Vector3(0, 0, -dir));
    for (let k = 0; k < 8; k++) tri(pos, c, r[k], r[(k + 1) % 8], inside);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

function box(w: number, h: number, d: number): THREE.BufferGeometry {
  return new THREE.BoxGeometry(w, h, d).toNonIndexed();
}

/** Flat plate from a 2D outline (x, z) with thickness along y. */
function plate(points: [number, number][], thickness: number): THREE.BufferGeometry {
  const shape = new THREE.Shape(points.map(([x, z]) => new THREE.Vector2(x, z)));
  const g = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: true, bevelSize: thickness * 0.4, bevelThickness: thickness * 0.3, bevelSegments: 1 });
  g.rotateX(Math.PI / 2); // shape XY → XZ, extrude along −Y
  g.translate(0, thickness / 2, 0);
  return g.index ? g.toNonIndexed() : g;
}

function bell(radius: number, length: number): THREE.BufferGeometry {
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    pts.push(new THREE.Vector2(radius * (0.55 + 0.45 * Math.pow(t, 1.6)), t * length));
  }
  pts.push(new THREE.Vector2(radius * 0.92, length));
  pts.push(new THREE.Vector2(radius * 0.5, length * 0.25));
  const g = new THREE.LatheGeometry(pts, 28);
  g.rotateX(Math.PI / 2); // axis +Y → +Z (exhaust points aft)
  return g.toNonIndexed();
}

function cylinder(r0: number, r1: number, h: number, seg = 16): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(r0, r1, h, seg).toNonIndexed();
}

/** World-scale box-projected UVs so plating density is the same on every part. */
function boxUV(g: THREE.BufferGeometry, metresPerTile: number): void {
  const p = g.attributes.position;
  const uv = new Float32Array(p.count * 2);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3();
  for (let i = 0; i < p.count; i += 3) {
    a.fromBufferAttribute(p, i);
    b.fromBufferAttribute(p, i + 1);
    c.fromBufferAttribute(p, i + 2);
    n.subVectors(b, a).cross(c.clone().sub(a));
    const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
    for (let k = 0; k < 3; k++) {
      const v = [a, b, c][k];
      let u: number, w: number;
      if (ax >= ay && ax >= az) { u = v.z; w = v.y; } else if (ay >= az) { u = v.x; w = v.z; } else { u = v.x; w = v.y; }
      uv[(i + k) * 2] = u / metresPerTile;
      uv[(i + k) * 2 + 1] = w / metresPerTile;
    }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

function clean(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g;
  out.clearGroups();
  // UVs are rebuilt after merging (world-scale box projection), so keep only position + normal.
  for (const name of Object.keys(out.attributes)) if (!['position', 'normal'].includes(name)) out.deleteAttribute(name);
  if (!out.attributes.normal) out.computeVertexNormals();
  return out;
}

let glowTex: THREE.Texture | null = null;
function glowTexture(): THREE.Texture {
  if (glowTex) return glowTex;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(255,255,255,0.7)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  glowTex = new THREE.CanvasTexture(c);
  return glowTex;
}

function registryTexture(text: string, sub: string, accent: THREE.Color): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 256;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, 1024, 256);
  g.fillStyle = `#${accent.getHexString()}`;
  g.fillRect(0, 0, 1024, 34);
  g.fillRect(0, 222, 1024, 10);
  g.fillStyle = 'rgba(235,236,238,0.92)';
  g.font = '600 120px "Chakra Petch", "Arial Narrow", sans-serif';
  g.textBaseline = 'middle';
  g.fillText(text, 16, 116);
  g.font = '500 44px "Chakra Petch", "Arial Narrow", sans-serif';
  g.fillStyle = 'rgba(235,236,238,0.7)';
  g.fillText(sub, 20, 190);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

const sec0 = (k: number) => [0.28, 0.12, -0.04][k] ?? 0;

// ---------- builder ----------

export function buildFrigate(p: FrigateParams, accentHex: string): FrigateModel {
  const rng = createSeededRandom(p.seed);
  const L = p.length, W = L * p.width, H = L * p.height;
  const parts: Record<MatKey, THREE.BufferGeometry[]> = { hull: [], dark: [], accent: [], engine: [], glow: [] };
  const add = (key: MatKey, g: THREE.BufferGeometry, m?: THREE.Matrix4) => {
    const geo = clean(g);
    if (m) geo.applyMatrix4(m);
    parts[key].push(geo);
  };
  const at = (x: number, y: number, z: number, rx = 0, ry = 0, rz = 0) =>
    new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(1, 1, 1));

  // Main hull sections (stern → bow).
  const bowZ = -L / 2;
  const sections: Section[] = [
    { z: L * 0.5, w: W * 0.92, h: H * 0.9, c: 0.18, y: 0 },
    { z: L * 0.36, w: W, h: H, c: 0.2, y: 0 },
    { z: L * 0.08, w: W * 0.9, h: H * 0.94, c: 0.22, y: 0 },
    { z: bowZ + L * p.prow, w: W * 0.62, h: H * 0.62, c: 0.26, y: -H * 0.06 },
    { z: bowZ + L * 0.05, w: W * 0.16, h: H * 0.2, c: 0.3, y: -H * 0.12 },
    { z: bowZ, w: W * 0.05, h: H * 0.08, c: 0.3, y: -H * 0.14 },
  ];
  add('hull', loft(sections));
  const sectionAt = (z: number): Section => {
    for (let i = 0; i < sections.length - 1; i++) {
      const a = sections[i], b = sections[i + 1];
      if (z <= a.z && z >= b.z) {
        const t = (a.z - z) / (a.z - b.z);
        return { z, w: a.w + (b.w - a.w) * t, h: a.h + (b.h - a.h) * t, c: a.c, y: a.y + (b.y - a.y) * t };
      }
    }
    return sections[0];
  };

  // Dorsal spine and ventral keel.
  add('hull', loft([
    { z: L * 0.42, w: W * 0.34, h: H * 0.3, c: 0.3, y: H * 0.55 },
    { z: L * 0.05, w: W * 0.3, h: H * 0.26, c: 0.3, y: H * 0.54 },
    { z: bowZ + L * p.prow * 0.9, w: W * 0.14, h: H * 0.1, c: 0.3, y: H * 0.34 },
  ]));
  add('dark', loft([
    { z: L * 0.4, w: W * 0.4, h: H * 0.2, c: 0.3, y: -H * 0.55 },
    { z: -L * 0.05, w: W * 0.36, h: H * 0.18, c: 0.3, y: -H * 0.53 },
    { z: bowZ + L * p.prow, w: W * 0.12, h: H * 0.06, c: 0.3, y: -H * 0.4 },
  ]));

  // Engineering block + nozzles.
  add('dark', box(W * 1.02, H * 0.92, L * 0.05), at(0, 0, L * 0.5 + L * 0.02));
  const engines: FrigateModel['engines'] = [];
  const { cols, rows } = p.engines;
  const er = Math.min((W * 0.9) / cols, (H * 0.85) / rows) * 0.42;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = (c - (cols - 1) / 2) * ((W * 0.9) / cols);
      const y = (r - (rows - 1) / 2) * ((H * 0.85) / rows);
      const z = L * 0.545;
      add('engine', bell(er, er * 1.6), at(x, y, z));
      add('glow', new THREE.CircleGeometry(er * 0.5, 24).toNonIndexed(), at(x, y, z + er * 0.42, 0, 0, 0));
      engines.push({ position: new THREE.Vector3(x, y, z + er * 1.6), radius: er });
    }
  }

  // Sponsons with their own engines.
  if (p.sponsons) {
    for (const s of [-1, 1]) {
      const sw = W * 0.34, x = s * (W * 0.5 + sw * 0.35);
      add('hull', loft([
        { z: L * 0.47, w: sw, h: H * 0.5, c: 0.25, y: -H * 0.08 },
        { z: L * 0.2, w: sw * 1.05, h: H * 0.52, c: 0.25, y: -H * 0.08 },
        { z: -L * 0.12, w: sw * 0.5, h: H * 0.28, c: 0.3, y: -H * 0.1 },
      ]).applyMatrix4(at(x, 0, 0)));
      add('accent', box(sw * 0.06, H * 0.4, L * 0.2), at(x + s * sw * 0.5, -H * 0.08, L * 0.2));
      const sr = sw * 0.3;
      add('engine', bell(sr, sr * 1.6), at(x, -H * 0.08, L * 0.47));
      add('glow', new THREE.CircleGeometry(sr * 0.5, 20).toNonIndexed(), at(x, -H * 0.08, L * 0.47 + sr * 0.42));
      engines.push({ position: new THREE.Vector3(x, -H * 0.08, L * 0.47 + sr * 1.6), radius: sr });
    }
  }

  // Wings / radiator plates.
  if (p.wingSpan > 0) {
    const span = L * p.wingSpan;
    for (const s of [-1, 1]) {
      const root = W * 0.45;
      const pts: [number, number][] = [
        [s * root, L * 0.34], [s * (root + span), L * 0.34 + p.wingSweep * 0.3], [s * (root + span), L * 0.2 + p.wingSweep], [s * root, L * 0.02],
      ];
      add('hull', plate(pts, Math.max(0.8, L * 0.008)), at(0, -H * 0.15, 0, 0, 0, s * -0.08));
      // Radiator fin rows on the wing.
      for (let k = 0; k < 6; k++) {
        const x = s * (root + span * (0.15 + k * 0.13));
        add('dark', box(span * 0.04, H * 0.04, L * 0.12), at(x, -H * 0.1, L * 0.25 + p.wingSweep * 0.3 * (k / 6)));
      }
      add('accent', box(span * 0.95, L * 0.004, L * 0.012), at(s * (root + span * 0.5), -H * 0.13, L * 0.33 + p.wingSweep * 0.15, 0, s * -Math.atan2(p.wingSweep * 0.3, span), 0));
    }
  }

  // Dorsal fin.
  if (p.dorsalFin) {
    add('hull', plate([[0, L * 0.48], [0, L * 0.2], [H * 0.9, L * 0.44], [H * 0.9, L * 0.5]], Math.max(0.8, L * 0.007)), at(0, H * 0.62, 0, 0, 0, Math.PI / 2));
    add('accent', box(L * 0.006, H * 0.5, L * 0.04), at(0, H * 1.25, L * 0.47));
  }

  // Bridge tower (separate group so the cockpit view can hide it).
  const bridgeParts: THREE.BufferGeometry[] = [];
  const bridgeGlow: THREE.BufferGeometry[] = [];
  const towerZ = L * 0.16;
  const towerBase = H * 0.66;
  const towerH = H * 0.42;
  bridgeParts.push(loft([
    { z: towerZ + L * 0.12, w: W * 0.34, h: towerH, c: 0.25, y: towerBase + towerH / 2 },
    { z: towerZ - L * 0.05, w: W * 0.3, h: towerH * 0.95, c: 0.25, y: towerBase + towerH / 2 },
  ]));
  const deckY = towerBase + towerH + H * 0.07;
  bridgeParts.push(loft([
    { z: towerZ + L * 0.1, w: W * 0.5, h: H * 0.14, c: 0.35, y: deckY },
    { z: towerZ - L * 0.07, w: W * 0.46, h: H * 0.13, c: 0.35, y: deckY },
    { z: towerZ - L * 0.1, w: W * 0.3, h: H * 0.06, c: 0.35, y: deckY - H * 0.02 },
  ]));
  // Window band wrapping the bridge front.
  bridgeGlow.push(clean(box(W * 0.42, H * 0.035, 0.3)).applyMatrix4(at(0, deckY, towerZ - L * 0.086, -0.5, 0, 0)));
  for (const s of [-1, 1]) bridgeGlow.push(clean(box(0.3, H * 0.03, L * 0.1)).applyMatrix4(at(s * W * 0.24, deckY, towerZ, 0, 0, 0)));
  // Antennas
  for (let i = 0; i < 4; i++) {
    const h = H * (0.25 + rng() * 0.35);
    bridgeParts.push(clean(cylinder(0.25, 0.4, h, 6)).applyMatrix4(at((rng() - 0.5) * W * 0.35, deckY + H * 0.07 + h / 2, towerZ + (rng() - 0.2) * L * 0.08)));
  }
  const cockpitEye = new THREE.Vector3(0, deckY + H * 0.01, towerZ - L * 0.078);

  // Turrets
  for (let i = 0; i < p.turrets; i++) {
    const z = -L * 0.02 - i * L * 0.11, y = H * 0.66 - i * H * 0.06;
    add('dark', cylinder(W * 0.1, W * 0.12, H * 0.08, 20), at(0, y, z));
    add('hull', box(W * 0.16, H * 0.1, W * 0.2), at(0, y + H * 0.08, z));
    for (const s of [-1, 1]) add('dark', cylinder(W * 0.012, W * 0.016, W * 0.36, 10), at(s * W * 0.04, y + H * 0.09, z - W * 0.25, Math.PI / 2, 0, 0));
  }

  // Sensor dish
  if (p.dish) {
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i <= 12; i++) {
      const r = (i / 12) * W * 0.3;
      pts.push(new THREE.Vector2(r, (r * r) / (W * 0.6)));
    }
    const dish = new THREE.LatheGeometry(pts, 32);
    add('hull', dish, at(0, H * 0.95, -L * 0.1, -0.6, 0, 0));
    add('dark', cylinder(W * 0.02, W * 0.03, H * 0.35, 8), at(0, H * 0.78, -L * 0.1));
  }

  // Accent stripes along the bow flanks.
  for (const s of [-1, 1]) {
    const z0 = bowZ + L * p.prow * 0.95, z1 = bowZ + L * 0.1;
    const a = sectionAt(z0), b = sectionAt(z1);
    const mid = (z0 + z1) / 2;
    const yaw = Math.atan2(((a.w - b.w) / 2) * s, z0 - z1);
    add('accent', box(0.4, H * 0.08, Math.hypot(z0 - z1, (a.w - b.w) / 2)), at(s * ((a.w + b.w) / 4 + 0.25), (a.y + b.y) / 2 + H * 0.12, mid, 0, yaw, 0));
  }

  // Hangar mouth (ventral)
  add('dark', box(W * 0.3, 0.5, L * 0.12), at(0, -H * 0.66, L * 0.18));

  // Materials
  const tex = hullTextures();
  const accent = new THREE.Color(accentHex);
  const mat = {
    hull: new THREE.MeshStandardMaterial({ color: 0xa4a8ae, map: tex.map, roughnessMap: tex.roughnessMap, normalMap: tex.normalMap, normalScale: new THREE.Vector2(0.9, 0.9), metalness: 0.55, roughness: 0.62, envMapIntensity: 0.7 }),
    dark: new THREE.MeshStandardMaterial({ color: 0x55585e, map: tex.map, roughnessMap: tex.roughnessMap, normalMap: tex.normalMap, metalness: 0.75, roughness: 0.5, envMapIntensity: 0.6 }),
    accent: new THREE.MeshStandardMaterial({ color: accent, map: tex.map, roughnessMap: tex.roughnessMap, normalMap: tex.normalMap, metalness: 0.35, roughness: 0.5 }),
    engine: new THREE.MeshStandardMaterial({ color: 0x3a3c40, metalness: 0.9, roughness: 0.35, emissive: new THREE.Color(0x331a08), side: THREE.DoubleSide }),
    glow: new THREE.MeshBasicMaterial({ color: new THREE.Color(0.7, 0.9, 1.4), side: THREE.DoubleSide }),
  };

  const root = new THREE.Group();
  let triangles = 0;
  for (const key of Object.keys(parts) as MatKey[]) {
    if (!parts[key].length) continue;
    const merged = mergeGeometries(parts[key], false)!;
    boxUV(merged, 14);
    const mesh = new THREE.Mesh(merged, mat[key]);
    mesh.name = key;
    root.add(mesh);
    triangles += merged.attributes.position.count / 3;
  }
  const bridge = new THREE.Group();
  bridge.name = 'bridge';
  const bridgeGeo = mergeGeometries(bridgeParts.map(clean), false)!;
  boxUV(bridgeGeo, 14);
  bridge.add(new THREE.Mesh(bridgeGeo, mat.hull));
  const glowGeo = mergeGeometries(bridgeGlow, false)!;
  bridge.add(new THREE.Mesh(glowGeo, new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 1.3, 0.9) })));
  root.add(bridge);
  triangles += bridgeGeo.attributes.position.count / 3;

  // Greebles: small instanced detail blocks, mostly hull-coloured, a few dark.
  const greebleGeo = new THREE.BoxGeometry(1, 1, 1);
  const count = Math.round(L * 1.6);
  const light = new THREE.InstancedMesh(greebleGeo, mat.hull, count);
  const darkG = new THREE.InstancedMesh(greebleGeo, mat.dark, count);
  let nl = 0, nd = 0;
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), v = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    const z = -L * 0.3 + rng() * L * 0.78;
    const sec = sectionAt(z);
    const face = rng();
    const size = 0.4 + Math.pow(rng(), 2.5) * L * 0.014;
    s.set(size * (0.6 + rng() * 1.4), size * (0.25 + rng() * 0.35), size * (1 + rng() * 2.5));
    if (face < 0.45) {
      v.set((rng() - 0.5) * (sec.w * 0.75), sec.y + sec.h / 2 + s.y / 2 - 0.1, z);
      q.identity();
      if (Math.abs(v.x) < W * 0.15 && z > -L * 0.2 && z < L * 0.4) continue; // keep the spine clean
    } else {
      const side = face < 0.72 ? 1 : -1;
      v.set(side * (sec.w / 2 + s.y / 2 - 0.1), sec.y + (rng() - 0.5) * sec.h * 0.5, z);
      q.setFromAxisAngle(new THREE.Vector3(0, 0, 1), side * -Math.PI / 2);
    }
    m.compose(v, q, s);
    if (rng() < 0.6) light.setMatrixAt(nl++, m);
    else darkG.setMatrixAt(nd++, m);
  }
  light.count = nl;
  darkG.count = nd;
  root.add(light, darkG);
  triangles += (nl + nd) * 12;

  // Conduits: long pipes running along the flanks.
  const pipeGeo: THREE.BufferGeometry[] = [];
  for (const sgn of [-1, 1]) {
    for (let k = 0; k < 3; k++) {
      const z0 = L * 0.34, z1 = -L * 0.1 + k * L * 0.05;
      const a = sectionAt(z0), b = sectionAt(z1);
      const yOff = -sec0(k) * H;
      const r = L * 0.0035 * (1 + (k % 2));
      const pa = new THREE.Vector3(sgn * (a.w / 2 + r), a.y + yOff, z0);
      const pb = new THREE.Vector3(sgn * (b.w / 2 + r), b.y + yOff, z1);
      const len = pa.distanceTo(pb);
      const g = new THREE.CylinderGeometry(r, r, len, 8).toNonIndexed();
      g.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
      g.lookAt(new THREE.Vector3().subVectors(pb, pa));
      g.translate((pa.x + pb.x) / 2, (pa.y + pb.y) / 2, (pa.z + pb.z) / 2);
      pipeGeo.push(clean(g));
    }
  }
  const pipes = mergeGeometries(pipeGeo, false)!;
  boxUV(pipes, 14);
  root.add(new THREE.Mesh(pipes, mat.dark));

  // Window strips on both flanks of the mid body.
  const winMat = new THREE.MeshStandardMaterial({ color: 0x0b0c0e, emissive: 0xffffff, emissiveMap: tex.emissiveWindows, emissiveIntensity: 2.2, metalness: 0.2, roughness: 0.4 });
  for (const sgn of [-1, 1]) {
    for (let row = 0; row < 2; row++) {
      const z0 = L * 0.32, z1 = -L * 0.12;
      const a = sectionAt(z0), b = sectionAt(z1);
      const len = Math.hypot(z0 - z1, (a.w - b.w) / 2);
      const t = tex.emissiveWindows.clone();
      t.needsUpdate = true;
      t.repeat.set(len / 26, 1 / 8);
      t.offset.set(rng(), row / 8 + 0.02);
      const wm = winMat.clone();
      wm.emissiveMap = t;
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(len, H * 0.05), wm);
      const yaw = Math.atan2(((a.w - b.w) / 2) * sgn, z0 - z1);
      strip.position.set(sgn * ((a.w + b.w) / 4 + 0.15), (a.y + b.y) / 2 + (row === 0 ? H * 0.18 : -H * 0.02), (z0 + z1) / 2);
      strip.rotation.set(0, sgn * Math.PI / 2 + yaw, 0);
      root.add(strip);
    }
  }

  // Registry decals on the bow flanks.
  const regTex = registryTexture(p.registry, 'STELLAR VANGUARD', accent);
  for (const sgn of [-1, 1]) {
    const z0 = bowZ + L * p.prow * 0.85, z1 = bowZ + L * p.prow * 0.35;
    const a = sectionAt(z0), b = sectionAt(z1);
    const len = Math.hypot(z0 - z1, (a.w - b.w) / 2);
    const decal = new THREE.Mesh(
      new THREE.PlaneGeometry(len, len / 4),
      new THREE.MeshStandardMaterial({ map: regTex, transparent: true, metalness: 0.3, roughness: 0.6, polygonOffset: true, polygonOffsetFactor: -2 }),
    );
    const yaw = Math.atan2(((a.w - b.w) / 2) * sgn, z0 - z1);
    decal.position.set(sgn * ((a.w + b.w) / 4 + 0.35), (a.y + b.y) / 2 - H * 0.04, (z0 + z1) / 2);
    decal.rotation.set(0, sgn * Math.PI / 2 + yaw, 0);
    root.add(decal);
  }

  // Navigation lights: red port, green starboard, white strobes.
  const navLights: THREE.Sprite[] = [];
  const strobes: THREE.Sprite[] = [];
  const spriteAt = (color: string, pos: THREE.Vector3, size: number) => {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTexture(), color: new THREE.Color(color).multiplyScalar(3), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
    sp.position.copy(pos);
    sp.scale.setScalar(size);
    root.add(sp);
    return sp;
  };
  const tipX = p.wingSpan > 0 ? W * 0.45 + L * p.wingSpan : W * 0.55;
  navLights.push(spriteAt('#ff2a2a', new THREE.Vector3(-tipX, -H * 0.15, L * 0.22), L * 0.03));
  navLights.push(spriteAt('#2aff5a', new THREE.Vector3(tipX, -H * 0.15, L * 0.22), L * 0.03));
  strobes.push(spriteAt('#ffffff', new THREE.Vector3(0, deckY + H * 0.4, towerZ), L * 0.035));
  strobes.push(spriteAt('#ffffff', new THREE.Vector3(0, -H * 0.62, -L * 0.2), L * 0.03));
  strobes.push(spriteAt('#ffffff', new THREE.Vector3(0, H * 0.1, bowZ + 0.5), L * 0.025));

  for (const o of root.children) {
    o.traverse((c) => {
      (c as THREE.Mesh).frustumCulled = false;
    });
  }

  const bbox = new THREE.Box3().setFromObject(root);
  return { root, bridge, engines, cockpitEye, size: bbox.getSize(new THREE.Vector3()), navLights, strobes, triangles };
}
